import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { Cabine } from "../componentes/FerramentaUi";
import { Segmentos } from "../componentes/FerramentaUi";
import {
  Alerta,
  BotaoCopiar,
  BotaoPrimario,
  BotaoSecundario,
  Cabecalho,
  LinhaDeValor,
  NumeroHeroi,
  TabelaRolante,
  emPercentual,
  emReais,
} from "../componentes/RadarSimplesUi";
import { mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import {
  apagarAbertura,
  criarAbertura,
  gravarItem,
  lerAbertura,
  lerAberturas,
  mensagemDaRecusa,
  renomearAbertura,
  type Abertura,
  type AberturaCompleta,
  type BlocoDeItens,
  type Catalogo,
  type Item,
} from "../lib/radarSimples";
import { ROTULO_DA_SECAO, secoesDe, textoDaResposta } from "../lib/aberturaDeEmpresa";

// O módulo Abertura do Radar Simples: um CLIENTE por vez, com o questionário que
// ele responde, as três planilhas de valor e os dois checklists de conferência.
//
// ---------------------------------------------------------------------------
// POR QUE TUDO PENDURA NUM CLIENTE
// ---------------------------------------------------------------------------
// Decisão de Victor em 04/09/2026. Na origem, o "link do questionário" era uma
// coisa (`checklist_links`, 309 deles, de 208 alunos) e os dois checklists e as
// três planilhas eram outra: viviam em `useState`, ou seja **sumiam ao recarregar
// a página**. O contador que abrisse uma segunda empresa perdia a primeira.
//
// Aqui a abertura É o cliente, e tudo se pendura nela. É o desenho que o dado já
// pedia: `checklist_links` sempre teve um rótulo escrito pelo aluno ("João da
// Silva - ME") — 246 dos 309 têm —, e esse rótulo já era o nome do cliente.
//
// ---------------------------------------------------------------------------
// A ABA É SUB-ROTA
// ---------------------------------------------------------------------------
// `/abertura/<uuid>/honorarios`, e não `useState`. O aluno volta pelo botão do
// navegador, manda ao colega o link da planilha de honorários daquele cliente, e
// recarregar não o joga para a primeira aba. É a decisão das abas do HUB DP.

export const BASE = "/ferramenta/radar-simples";

type Aba = { seg: string; nome: string; bloco: BlocoDeItens | null };

const ABAS: Aba[] = [
  { seg: "questionario", nome: "Questionário", bloco: null },
  { seg: "custo-abertura", nome: "Custo de abertura", bloco: "custo-abertura" },
  { seg: "custo-mensal", nome: "Custo mensal", bloco: "custo-mensal" },
  { seg: "honorarios", nome: "Honorários", bloco: "honorarios" },
  { seg: "obrigacoes", nome: "Obrigações", bloco: "obrigacoes" },
  { seg: "entrega", nome: "Entrega de documentos", bloco: "entrega" },
];

export default function RadarAbertura({
  curso,
  catalogo,
  aberturaId,
}: {
  curso: string;
  catalogo: Catalogo;
  aberturaId: string | null;
}) {
  const params = useParams();
  const resto = (params["*"] ?? "").split("/").filter(Boolean);
  const aba = resto[2] ?? "questionario";

  if (!aberturaId) return <ListaDeClientes curso={curso} />;
  return <FichaDoCliente curso={curso} catalogo={catalogo} id={aberturaId} aba={aba} />;
}

function comCurso(caminho: string, curso: string): string {
  return `${caminho}?curso=${encodeURIComponent(curso)}`;
}

// ---------------------------------------------------------------------------
// A lista de clientes
// ---------------------------------------------------------------------------

function ListaDeClientes({ curso }: { curso: string }) {
  const [estado, setEstado] = useState<
    { fase: "carregando" } | { fase: "pronto"; aberturas: Abertura[] } | { fase: "erro"; motivo: string }
  >({ fase: "carregando" });
  const [rotulo, setRotulo] = useState("");
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    const r = await lerAberturas(curso);
    setEstado(
      r.fase === "pronto"
        ? { fase: "pronto", aberturas: r.dados }
        : { fase: "erro", motivo: r.fase === "recusado" ? r.motivo : "erro" },
    );
  }, [curso]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const respondidos = estado.fase === "pronto" ? estado.aberturas.filter((a) => a.status === "respondido").length : 0;

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(BASE, curso), rotulo: "Radar Simples" }}
        rotulo="Módulo"
        titulo="Abertura de empresa"
        descricao="Um cliente por vez: o questionário que ele responde, os custos, os honorários e os dois checklists de conferência."
        fatos={
          estado.fase === "pronto" && estado.aberturas.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-cf bg-white/10 px-3 py-2.5 backdrop-blur-sm">
                <p className="text-[11px] uppercase tracking-[0.1em] text-navy-mute">Clientes</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-navy-ink">
                  {estado.aberturas.length}
                </p>
              </div>
              <div className="rounded-cf bg-white/10 px-3 py-2.5 backdrop-blur-sm">
                <p className="text-[11px] uppercase tracking-[0.1em] text-navy-mute">Responderam</p>
                <p className="mt-0.5 text-xl font-bold tabular-nums text-navy-ink">{respondidos}</p>
              </div>
            </div>
          ) : undefined
        }
      />

      <section className="rounded-cf border border-borda bg-superficie p-4">
        <h2 className="text-sm font-semibold text-titulo">Novo cliente</h2>
        <p className="mt-1 text-sm text-cinza">
          Dê um nome para você se organizar. Ele aparece para o cliente no topo do questionário.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[16rem] flex-1">
            <label htmlFor="rotulo" className="sr-only">
              Nome do cliente
            </label>
            <input
              id="rotulo"
              value={rotulo}
              onChange={(e) => setRotulo(e.target.value)}
              placeholder="Ex.: João da Silva — padaria"
              maxLength={120}
              className="w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento focus:ring-2 focus:ring-acento/30"
            />
          </div>
          <BotaoPrimario
            data-criar
            desligado={criando}
            aoClicar={async () => {
              setCriando(true);
              setErro(null);
              const r = await criarAbertura(curso, rotulo);
              setCriando(false);
              if (r.ok) {
                setRotulo("");
                void recarregar();
              } else {
                setErro(mensagemDaRecusa(r.motivo, r.mensagem));
              }
            }}
          >
            {criando ? "Criando…" : "Criar cliente"}
          </BotaoPrimario>
        </div>
        {erro && <p className="mt-2 text-sm text-erro-tinta">{erro}</p>}
      </section>

      {estado.fase === "carregando" && <Carregando />}
      {estado.fase === "erro" && (
        <Alerta titulo="Não conseguimos carregar seus clientes">
          {mensagemDaRecusa(estado.motivo)}
        </Alerta>
      )}

      {estado.fase === "pronto" && estado.aberturas.length === 0 && (
        <div className="rounded-cf border border-borda bg-chip px-4 py-6 text-center text-sm text-cinza">
          Nenhum cliente ainda. Crie o primeiro acima — você recebe um link para mandar a ele.
        </div>
      )}

      {estado.fase === "pronto" && estado.aberturas.length > 0 && (
        <ul className="space-y-3" data-clientes>
          {estado.aberturas.map((a) => (
            <li
              key={a.id}
              data-cliente={a.id}
              className="rounded-cf border border-borda bg-superficie p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={comCurso(`${BASE}/abertura/${a.id}`, curso)}
                    className="font-medium text-titulo underline decoration-borda underline-offset-2 hover:decoration-link"
                  >
                    {a.rotulo ?? "Cliente sem nome"}
                  </Link>
                  <p className="mt-0.5 text-xs text-cinza">
                    Criado em {new Date(a.criado_em).toLocaleDateString("pt-BR")}
                    {a.itens_marcados > 0 && ` · ${a.itens_marcados} itens preenchidos`}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {a.status === "respondido" ? (
                    <span className="rounded-full border border-aviso-borda bg-aviso-fundo px-2.5 py-1 text-xs font-medium text-aviso-tinta">
                      Respondeu
                      {a.respostas > 1 && ` (${a.respostas} envios)`}
                    </span>
                  ) : (
                    <span className="rounded-full border border-borda bg-chip px-2.5 py-1 text-xs text-cinza">
                      Aguardando resposta
                    </span>
                  )}
                  <BotaoCopiar texto={enderecoDoFormulario(a.token)} rotulo="Copiar link" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * O endereço público do questionário.
 *
 * Montado sobre `window.location.origin`, e não sobre um domínio escrito à mão:
 * o hub atende em mais de um endereço (produção, prévia, a máquina de quem
 * desenvolve), e um domínio cravado mandaria o cliente para o lugar errado em
 * dois dos três.
 */
function enderecoDoFormulario(token: string): string {
  return `${window.location.origin}/formulario/${token}`;
}

// ---------------------------------------------------------------------------
// A ficha de um cliente
// ---------------------------------------------------------------------------

function FichaDoCliente({
  curso,
  catalogo,
  id,
  aba,
}: {
  curso: string;
  catalogo: Catalogo;
  id: string;
  aba: string;
}) {
  const [estado, setEstado] = useState<
    { fase: "carregando" } | { fase: "pronto"; dados: AberturaCompleta } | { fase: "recusado"; motivo: string } | { fase: "erro" }
  >({ fase: "carregando" });

  const recarregar = useCallback(async () => {
    const r = await lerAbertura(curso, id);
    setEstado(r.fase === "pronto" ? { fase: "pronto", dados: r.dados } : r);
  }, [curso, id]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // O estado dos itens mora AQUI, no pai, e não em cada aba: o mesmo número
  // aparece na aba, no total e no rótulo, e estado dentro de uma peça deixa as
  // outras duas presas no valor do carregamento — foi o "0 de 3" do Leitor com
  // "1 de 3 marcados" embaixo.
  //
  // E ele fica ANTES dos returns condicionais. Estava depois, e a tela inteira
  // morria com React #310 ("Rendered more hooks than during the previous
  // render"): na primeira passagem o componente retornava em `carregando` e o
  // `useMemo` não rodava; na segunda, rodava. Nem o `tsc` nem o `vite build`
  // veem isso — só a tela, e a prova em Chrome foi quem pegou.
  const itens = estado.fase === "pronto" ? estado.dados.itens : [];
  const porItem = useMemo(() => new Map(itens.map((i) => [i.item_id, i])), [itens]);

  if (estado.fase === "carregando") return <Carregando />;
  if (estado.fase === "recusado" || estado.fase === "erro") {
    return (
      <div className="space-y-4">
        <Cabine
          trilha={{ para: comCurso(`${BASE}/abertura`, curso), rotulo: "Abertura de empresa" }}
          rotulo="Cliente"
          titulo="Não encontrado"
        />
        <Alerta titulo="Este cliente não abriu">
          {estado.fase === "recusado"
            ? mensagemDaRecusa(estado.motivo)
            : "Tente recarregar a página."}
        </Alerta>
      </div>
    );
  }

  const { abertura, respostas } = estado.dados;
  const abaAtual = ABAS.find((a) => a.seg === aba) ?? ABAS[0];

  return (
    <div className="space-y-5">
      <Cabine
        trilha={{ para: comCurso(`${BASE}/abertura`, curso), rotulo: "Abertura de empresa" }}
        rotulo="Cliente"
        titulo={abertura.rotulo ?? "Cliente sem nome"}
        descricao={
          abertura.status === "respondido"
            ? `Respondeu o questionário em ${new Date(abertura.respondido_em ?? abertura.criado_em).toLocaleDateString("pt-BR")}.`
            : "Ainda não respondeu ao questionário."
        }
      />

      <nav className="flex flex-wrap gap-2" aria-label="Seções do cliente">
        {ABAS.map((a) => {
          const doBloco = a.bloco
            ? catalogo.itens.filter((i) => i.bloco === a.bloco)
            : [];
          const marcados = doBloco.filter((i) => porItem.get(i.id)?.marcado).length;
          return (
            <Link
              key={a.seg}
              to={comCurso(`${BASE}/abertura/${id}/${a.seg}`, curso)}
              data-aba={a.seg}
              aria-current={abaAtual.seg === a.seg ? "page" : undefined}
              className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
                abaAtual.seg === a.seg
                  ? "bg-acento text-acento-tinta"
                  : "border border-borda bg-superficie text-tinta hover:border-acento/40"
              }`}
            >
              {a.nome}
              {a.bloco && doBloco.length > 0 && (
                <span className="ml-1.5 opacity-80">
                  {marcados}/{doBloco.length}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {abaAtual.seg === "questionario" ? (
        <AbaQuestionario abertura={abertura} respostas={respostas} />
      ) : abaAtual.bloco === "obrigacoes" || abaAtual.bloco === "entrega" ? (
        <AbaChecklist
          curso={curso}
          aberturaId={id}
          bloco={abaAtual.bloco}
          itens={catalogo.itens.filter((i) => i.bloco === abaAtual.bloco)}
          porItem={porItem}
          aoGravar={recarregar}
        />
      ) : (
        <AbaPlanilha
          curso={curso}
          aberturaId={id}
          bloco={abaAtual.bloco as BlocoDeItens}
          itens={catalogo.itens.filter((i) => i.bloco === abaAtual.bloco)}
          porItem={porItem}
          aoGravar={recarregar}
        />
      )}

      <div className="border-t border-borda pt-4">
        <ZonaDeRisco curso={curso} abertura={abertura} temResposta={respostas.length > 0} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: o questionário
// ---------------------------------------------------------------------------

function AbaQuestionario({
  abertura,
  respostas,
}: {
  abertura: AberturaCompleta["abertura"];
  respostas: AberturaCompleta["respostas"];
}) {
  const endereco = enderecoDoFormulario(abertura.token);

  return (
    <div className="space-y-4">
      <section className="rounded-cf border border-borda bg-superficie p-4">
        <h2 className="text-sm font-semibold text-titulo">Link do questionário</h2>
        <p className="mt-1 text-sm text-cinza">
          Mande este endereço ao cliente. Ele preenche sem precisar de conta na Contabilidade
          Facilitada, e o formulário aceita UMA resposta.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code
            data-endereco
            className="min-w-0 flex-1 overflow-x-auto rounded-cf bg-chip px-3 py-2 text-xs text-tinta"
          >
            {endereco}
          </code>
          <BotaoCopiar texto={endereco} />
        </div>
      </section>

      {respostas.length === 0 && (
        <div className="rounded-cf border border-borda bg-chip px-4 py-6 text-center text-sm text-cinza">
          O cliente ainda não respondeu. Assim que ele enviar, as respostas aparecem aqui.
        </div>
      )}

      {/* TODAS as respostas, e não só a mais recente. Dois links da origem têm
          duas, de clientes que preencheram o formulário inteiro — e a tela de lá
          mostrava "Nenhuma resposta encontrada" nos dois, porque o
          `.maybeSingle()` devolve 406 com duas linhas. */}
      {respostas.length > 1 && (
        <Alerta titulo={`Este cliente enviou ${respostas.length} vezes`}>
          Os envios aparecem do mais novo para o mais antigo. Compare antes de usar.
        </Alerta>
      )}

      {respostas.map((r, i) => (
        <section key={r.id} data-resposta={i} className="rounded-cf border border-borda bg-superficie p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold text-titulo">
              {respostas.length > 1 ? `Envio ${respostas.length - i}` : "Respostas"}
            </h2>
            <time className="text-xs text-cinza" dateTime={r.criado_em}>
              {new Date(r.criado_em).toLocaleString("pt-BR")}
            </time>
          </div>

          <div className="mt-3 space-y-4">
            {secoesDe(r.respostas).map((s) => (
              <div key={s.chave} data-secao={s.chave}>
                <h3 className="text-sm font-semibold text-titulo">{ROTULO_DA_SECAO[s.chave] ?? s.chave}</h3>
                <dl className="mt-1.5 space-y-1">
                  {s.campos.map((c) => (
                    <div key={c.chave} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                      <dt className="shrink-0 text-sm text-cinza sm:w-64">{c.rotulo}</dt>
                      <dd className="text-sm text-tinta">{textoDaResposta(c.valor)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: um checklist
// ---------------------------------------------------------------------------

type PorItem = Map<string, { marcado: boolean; quantidade: number; valor_unitario: number }>;

function AbaChecklist({
  curso,
  aberturaId,
  bloco,
  itens,
  porItem,
  aoGravar,
}: {
  curso: string;
  aberturaId: string;
  bloco: BlocoDeItens;
  itens: Item[];
  porItem: PorItem;
  aoGravar: () => Promise<void>;
}) {
  const [erroDaLinha, setErroDaLinha] = useState<Record<string, string>>({});
  const secoes = agruparPorSecao(itens);
  const marcados = itens.filter((i) => porItem.get(i.id)?.marcado).length;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-tinta">
            {marcados} de {itens.length} conferidos
          </span>
          <span className="text-sm tabular-nums text-cinza">
            {itens.length > 0 ? emPercentual(Math.round((marcados / itens.length) * 100), 0) : "—"}
          </span>
        </div>
        <Segmentos
          total={itens.length}
          acesos={marcados}
          rotulo="Itens conferidos"
          sobreNavy={false}
          mapa={itens.map((i) => porItem.get(i.id)?.marcado === true)}
        />
      </div>

      {secoes.map((s) => (
        <section key={s.nome} data-secao={s.nome} className="rounded-cf border border-borda bg-superficie p-4">
          <h3 className="text-sm font-semibold text-titulo">
            <span className="mr-1.5 text-cinza">{s.ordem}.</span>
            {s.nome}
          </h3>
          {/* O aviso da seção. `ALERTA` é a marca que a carga põe na seção que a
              origem desenhava com borda vermelha — vira a família `alerta`
              (laranja), nunca `aviso`, que nesta casa é VERDE. */}
          {s.aviso === "ALERTA" ? (
            <p className="mt-2 rounded-cf border border-alerta-tinta/40 bg-alerta-fundo px-2.5 py-1.5 text-xs text-alerta-tinta">
              Itens que o escritório costuma monitorar depois da abertura.
            </p>
          ) : (
            s.aviso && <p className="mt-1.5 text-xs italic text-cinza">{s.aviso}</p>
          )}

          <ul className="mt-3 space-y-2">
            {s.itens.map((item) => {
              const marcado = porItem.get(item.id)?.marcado === true;
              return (
                <li key={item.id}>
                  <label className="flex items-start gap-2.5 text-sm">
                    <input
                      type="checkbox"
                      data-item={item.id}
                      checked={marcado}
                      onChange={async () => {
                        const r = await gravarItem(curso, aberturaId, item.id, !marcado);
                        // Erro de UMA marcação fica NA LINHA. Sem leitura não há
                        // tela; sem uma escrita, há tela e falta um tique — e
                        // trocar a página inteira por uma mensagem apagaria o
                        // checklist que o contador está lendo.
                        setErroDaLinha((e) =>
                          r.ok
                            ? { ...e, [item.id]: "" }
                            : { ...e, [item.id]: mensagemDaRecusa(r.motivo, r.mensagem) },
                        );
                        if (r.ok) await aoGravar();
                      }}
                      className="mt-0.5 size-4 shrink-0 accent-acento"
                    />
                    <span className={marcado ? "text-cinza line-through" : "text-tinta"}>
                      {item.texto}
                    </span>
                  </label>
                  {erroDaLinha[item.id] && (
                    <p className="ml-6.5 mt-0.5 text-xs text-erro-tinta">{erroDaLinha[item.id]}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <p className="text-xs text-cinza">
        {bloco === "entrega"
          ? "Use “Imprimir” do navegador para gerar o recibo de entrega em PDF."
          : "O que você marca fica guardado neste cliente e não some ao fechar a página."}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba: uma planilha
// ---------------------------------------------------------------------------

function AbaPlanilha({
  curso,
  aberturaId,
  bloco,
  itens,
  porItem,
  aoGravar,
}: {
  curso: string;
  aberturaId: string;
  bloco: BlocoDeItens;
  itens: Item[];
  porItem: PorItem;
  aoGravar: () => Promise<void>;
}) {
  const secoes = agruparPorSecao(itens);
  const [erroDaLinha, setErroDaLinha] = useState<Record<string, string>>({});

  // O que entra no total: o item marcado. Quem nunca foi tocado usa o padrão do
  // catálogo — assim a planilha já abre com a sugestão da casa, como na origem.
  const estadoDe = (item: Item) => {
    const gravado = porItem.get(item.id);
    if (gravado) return gravado;
    return {
      marcado: item.ligado_por_padrao,
      quantidade: 1,
      valor_unitario: item.valor_sugerido ?? 0,
    };
  };

  const totalDe = (item: Item) => {
    const e = estadoDe(item);
    return e.marcado ? e.quantidade * e.valor_unitario : 0;
  };
  const total = itens.reduce((s, i) => s + totalDe(i), 0);

  const gravar = async (item: Item, mudanca: Partial<{ marcado: boolean; quantidade: number; valor: number }>) => {
    const atual = estadoDe(item);
    const r = await gravarItem(
      curso,
      aberturaId,
      item.id,
      mudanca.marcado ?? atual.marcado,
      mudanca.quantidade ?? atual.quantidade,
      mudanca.valor ?? atual.valor_unitario,
    );
    setErroDaLinha((e) =>
      r.ok ? { ...e, [item.id]: "" } : { ...e, [item.id]: mensagemDaRecusa(r.motivo, r.mensagem) },
    );
    if (r.ok) await aoGravar();
  };

  const rotuloDoQualificador = bloco === "honorarios" ? "Cobrança" : "Natureza";

  return (
    <div className="space-y-4">
      <NumeroHeroi
        rotulo={
          bloco === "custo-abertura"
            ? "Custo estimado de abertura"
            : bloco === "custo-mensal"
              ? "Custo mensal estimado"
              : "Honorários mensais"
        }
        valor={emReais(total)}
        apoio={`${itens.filter((i) => estadoDe(i).marcado).length} de ${itens.length} itens incluídos.`}
      />

      {bloco === "honorarios" && (
        <p className="text-xs text-cinza">
          Os valores que já vêm preenchidos são <strong>sugestão</strong>, não tabela oficial —
          honorário se define por escritório, por região e por complexidade. Troque à vontade.
        </p>
      )}

      {secoes.map((s) => {
        const subtotal = s.itens.reduce((acc, i) => acc + totalDe(i), 0);
        return (
          <section key={s.nome} data-secao={s.nome} className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-titulo">{s.nome}</h3>
              <span className="text-sm tabular-nums text-cinza">{emReais(subtotal)}</span>
            </div>
            <TabelaRolante rotulo={s.nome}>
              <Cabecalho colunas={["Item", rotuloDoQualificador, "Incluir", "Qtd.", "Valor", "Total"]} />
              <tbody>
                {s.itens.map((item) => {
                  const e = estadoDe(item);
                  return (
                    <tr key={item.id} data-item={item.id} className="border-b border-borda last:border-0">
                      <td className="px-3 py-2 text-tinta">{item.texto}</td>
                      <td className="px-3 py-2 text-right text-xs text-cinza">{item.qualificador ?? "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="checkbox"
                          aria-label={`Incluir ${item.texto}`}
                          data-incluir={item.id}
                          checked={e.marcado}
                          onChange={() => gravar(item, { marcado: !e.marcado })}
                          className="size-4 accent-acento"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          aria-label={`Quantidade de ${item.texto}`}
                          data-quantidade={item.id}
                          value={e.quantidade}
                          disabled={!e.marcado}
                          onChange={(ev) =>
                            gravar(item, { quantidade: Math.max(0, Number(ev.target.value) || 0) })
                          }
                          className="w-16 rounded border border-borda bg-superficie px-2 py-1 text-right text-sm tabular-nums text-tinta disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <CampoDeValorDaLinha
                          item={item}
                          valor={e.valor_unitario}
                          desligado={!e.marcado}
                          aoMudar={(v) => gravar(item, { valor: v })}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-tinta">
                        {emReais(totalDe(item))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </TabelaRolante>
            {s.itens.some((i) => erroDaLinha[i.id]) && (
              <p className="text-xs text-erro-tinta">
                {s.itens.map((i) => erroDaLinha[i.id]).find(Boolean)}
              </p>
            )}
          </section>
        );
      })}

      <LinhaDeValor rotulo="Total" valor={emReais(total)} forte />
    </div>
  );
}

/**
 * O campo de valor de uma linha da planilha.
 *
 * Só grava quando o campo PERDE o foco (ou o usuário aperta Enter): gravar a
 * cada tecla mandaria uma ida ao servidor por dígito, e num campo de moeda por
 * centavos "R$ 1.200,00" são seis.
 */
function CampoDeValorDaLinha({
  item,
  valor,
  desligado,
  aoMudar,
}: {
  item: Item;
  valor: number;
  desligado: boolean;
  aoMudar: (v: number) => void;
}) {
  const [texto, setTexto] = useState(() => moedaDeNumero(valor));
  const ultimoGravado = useRef(valor);

  useEffect(() => {
    if (valor !== ultimoGravado.current) {
      ultimoGravado.current = valor;
      setTexto(moedaDeNumero(valor));
    }
  }, [valor]);

  const enviar = () => {
    const n = mascararMoeda(texto).numero ?? 0;
    if (n !== ultimoGravado.current) {
      ultimoGravado.current = n;
      aoMudar(n);
    }
  };

  return (
    <input
      inputMode="numeric"
      aria-label={`Valor de ${item.texto}`}
      data-valor={item.id}
      value={texto}
      disabled={desligado}
      onChange={(e) => setTexto(mascararMoeda(e.target.value).texto)}
      onBlur={enviar}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          enviar();
        }
      }}
      className="w-28 rounded border border-borda bg-superficie px-2 py-1 text-right text-sm tabular-nums text-tinta disabled:opacity-50"
    />
  );
}

// ---------------------------------------------------------------------------
// Renomear e apagar
// ---------------------------------------------------------------------------

function ZonaDeRisco({
  curso,
  abertura,
  temResposta,
}: {
  curso: string;
  abertura: AberturaCompleta["abertura"];
  temResposta: boolean;
}) {
  const [rotulo, setRotulo] = useState(abertura.rotulo ?? "");
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="space-y-3">
      <details className="rounded-cf border border-borda bg-superficie p-4">
        <summary className="cursor-pointer text-sm font-medium text-cinza">
          Renomear ou remover este cliente
        </summary>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="novo-rotulo" className="block text-sm font-medium text-tinta">
              Nome do cliente
            </label>
            <input
              id="novo-rotulo"
              value={rotulo}
              onChange={(e) => setRotulo(e.target.value)}
              maxLength={120}
              className="mt-1 w-full rounded-cf border border-borda bg-superficie px-3 py-2 text-tinta outline-none focus:border-acento"
            />
          </div>
          <BotaoSecundario
            data-renomear
            aoClicar={async () => {
              const r = await renomearAbertura(curso, abertura.id, rotulo);
              setAviso(r.ok ? "Nome atualizado." : mensagemDaRecusa(r.motivo, r.mensagem));
            }}
          >
            Salvar nome
          </BotaoSecundario>
        </div>

        <div className="mt-4 border-t border-borda pt-3">
          {temResposta ? (
            // Não é um botão desligado com "opacity": é a ausência do botão, com
            // a razão escrita. Desabilitado permanente pede desenho próprio, e a
            // pessoa olhando para ele precisa entender por quê.
            <p className="text-sm text-cinza">
              Este cliente já respondeu ao questionário, então ele não pode ser removido — o que ele
              preencheu (nome, CPF, endereço, capital social) fica guardado. Se precisar mesmo
              remover, fale com o suporte.
            </p>
          ) : confirmando ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-erro-tinta">Remover este cliente? Não tem volta.</span>
              <BotaoSecundario
                data-confirmar-remover
                aoClicar={async () => {
                  const r = await apagarAbertura(curso, abertura.id);
                  if (r.ok) window.location.assign(`${BASE}/abertura?curso=${encodeURIComponent(curso)}`);
                  else setAviso(mensagemDaRecusa(r.motivo, r.mensagem));
                }}
              >
                Sim, remover
              </BotaoSecundario>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="text-sm text-cinza underline"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-remover
              onClick={() => setConfirmando(true)}
              className="text-sm text-erro-tinta underline"
            >
              Remover este cliente
            </button>
          )}
        </div>

        {aviso && <p className="mt-2 text-sm text-cinza">{aviso}</p>}
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agrupar por seção
// ---------------------------------------------------------------------------

type Secao = { nome: string; ordem: number; aviso: string | null; itens: Item[] };

/**
 * Agrupa os itens de um bloco por seção, respeitando a ordem do catálogo.
 *
 * A ORDEM sai de `secao_ordem` e `ordem`, que vêm do banco já ordenados — não é
 * refeita aqui. Reordenar em JavaScript criaria uma segunda ordem, e a que
 * divergisse em silêncio seria a que ninguém compara.
 */
function agruparPorSecao(itens: Item[]): Secao[] {
  const mapa = new Map<number, Secao>();
  for (const i of itens) {
    if (!mapa.has(i.secao_ordem)) {
      mapa.set(i.secao_ordem, {
        nome: i.secao,
        ordem: i.secao_ordem,
        aviso: i.aviso_da_secao,
        itens: [],
      });
    }
    mapa.get(i.secao_ordem)!.itens.push(i);
  }
  return [...mapa.values()].sort((a, b) => a.ordem - b.ordem);
}
